<template>
  <Container as="nav" aria-label="Hoofdmenu" class="w-full py-4 flex justify-between items-center z-40 relative">
    <div class="w-full hidden xl:flex items-end justify-between" data-component="NavigationBar">
      <div class="flex-wrap flex-col items-start justify-end">
        <div class="flex items-center gap-2">
          <CustomNuxtLink to="/" aria-label="Home">
            <Logo class="h-6 md:w-auto md:h-auto md:max-w-[87.5px]" variant="dark" />
          </CustomNuxtLink>
          <CustomNuxtLink
            v-if="subDomain?.title"
            :to="subDomain.url"
            :aria-label="subDomain.title"
            class="text-[20px] font-bold text-primary-100 leading-none">
            {{ subDomain.title }}
          </CustomNuxtLink>
        </div>
        <CustomNuxtLink v-if="domain" :to="domain?.url" :aria-label="domain.domain">
          <DomainLabel v-if="domain?.domain" class="h-6 md:h-[35px] md:w-auto" :domain="domain.domain" />
        </CustomNuxtLink>
      </div>
      <div class="flex flex-row items-center gap-[15px]">
        <div class="flex flex-col items-center gap-[17px]">
          <ul
            class="flex items-center self-end gap-[20px]"
            data-navigation="sub"
            aria-label="Subnavigatie">
            <li v-for="(item, index) in topNav" :key="item.title ?? `topnav-${index}`" class="flex items-center">
              <Link
                :to="item.url"
                :has-icon="item.external"
                variant="navigation-sub"
                class="group gap-1 text-primary-70"
                :aria-label="item.external ? `${item.title} (opent in nieuw venster)` : item.title">
                {{ item.title }}
                <svg
                  v-if="item.external"
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1.38086 1.38135H6.61895V6.61944"
                    stroke="currentColor"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M1.38086 6.61944L6.61895 1.38135"
                    stroke="currentColor"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </Link>
            </li>
            <li v-if="search" class="flex items-center">
              <CustomNuxtLink
                :to="search.url"
                class="w-[18px] h-[18px] text-primary-80 hover:text-accent-100 transition-colors"
                aria-label="Zoeken">
                <SvgComponent icon="search" class="h-[18px] w-[18px]" />
              </CustomNuxtLink>
            </li>
          </ul>
          <div class="flex flex-row gap-[15px] h-9 ml-auto">
            <ul
              class="flex gap-[30px] items-center"
              data-navigation="main"
              aria-label="Hoofdnavigatie">
              <li
                v-for="(item, index) in mainNav.filter(
                  i => i.type === 'corporateSubhomeNavigationLink' || i.type === 'sharedLink',
                )"
                :key="item.title ?? `mainnav-${index}`"
                class="flex items-center">
                <DomainLink
                  v-if="item.type === 'corporateSubhomeNavigationLink'"
                  class="py-2"
                  :domain="item.domain ?? 'head-office'"
                  :active="item.domain === domain?.domain"
                  :url="item.url ?? ''" />
                <Link
                  v-else-if="item.type === 'sharedLink'"
                  :to="item.url ?? ''"
                  variant="caption-bold"
                  class="font-bold tracking-tight"
                  :aria-label="item.title">
                  {{ item.title }}</Link
                >
              </li>
            </ul>

            <ul
              v-if="
                mainNav.filter(i => i.type !== 'corporateSubhomeNavigationLink' && i.type !== 'sharedLink').length > 0
              "
              class="flex items-center ml-[15px] gap-[15px]">
              <li
                v-for="(item, index) in mainNav.filter(
                  i => i.type !== 'corporateSubhomeNavigationLink' && i.type !== 'sharedLink',
                )"
                :key="`button-${item.title ?? index}`"
                class="flex items-center">
                <Button
                  variant="top-nav"
                  as="NuxtLink"
                  size="xs"
                  :to="item.url ?? ''"
                  class="text-[14px]">
                  {{ item.title }}
                </Button>
              </li>
            </ul>

            <ul v-if="hasAsideNavigation" class="flex gap-7 items-center">
              <li class="flex items-center">
                <MenuButton
                  variant="black"
                  :is-open="openAside"
                  class="transition-colors duration-300"
                  @click="openAside = !openAside" />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full flex xl:hidden justify-between items-center">
      <div class="flex items-center flex-column">
        <CustomNuxtLink to="/" aria-label="Home">
          <Logo class="h-6 w-[60px] shrink-0" variant="dark" />
        </CustomNuxtLink>
        <CustomNuxtLink v-if="domain" :to="domain?.url" :aria-label="domain.domain">
          <DomainLabel v-if="domain?.domain" class="h-6" :domain="domain.domain" />
        </CustomNuxtLink>
        <CustomNuxtLink
          v-if="subDomain?.title"
          :to="subDomain.url"
          :aria-label="subDomain.title"
          class="ml-2 text-[14px] font-bold text-primary-100 leading-none">
          {{ subDomain.title }}
        </CustomNuxtLink>
      </div>
      <div class="flex gap-4 py-4 items-center relative">
        <CustomNuxtLink
          v-if="search"
          :to="search.url"
          class="px-3 right-8 absolute text-primary-80 hover:text-accent-100 transition-colors">
          <SvgComponent icon="search" class="h-[18px] w-[18px]" />
        </CustomNuxtLink>
        <MenuButton
          :variant="model || hasScrolled ? 'white' : 'black'"
          :is-open="model"
          class="group hover:cursor-pointer fixed right-4 transition-colors duration-300"
          :class="{ 'px-3 py-3 bg-primary-90 text-white rounded-md': hasScrolled }"
          @click="model = !model" />
      </div>
    </div>
  </Container>
</template>

<script setup lang="ts">
import { useCorporateNavigation } from '@corporate/composables'
import {
  Container,
  Logo,
  Link,
  CustomNuxtLink,
  DomainLink,
  DomainLabel,
  MenuButton,
  Button,
} from '@gro-up/ui/primitives'
import { useWindowScroll } from '@vueuse/core'
import { computed } from 'vue'

import SvgComponent from '../../../../../packages/ui/src/primitives/SvgComponent/SvgComponent.vue'

const { y } = useWindowScroll()
const SCROLL_OFFSET = 68
const hasScrolled = computed(() => y.value > SCROLL_OFFSET)

const model = defineModel<boolean>({ default: false })
const openAside = defineModel<boolean>('openAside', { default: false })

const { topNav, mainNav, domain, subDomain, search, hasAsideNavigation } = useCorporateNavigation()
</script>
